// Import real fetched data
import REAL_DATA from '../data/real-state-data.js';

class DataService {
    constructor() {
        this.cache = new Map();
        this.cacheExpiry = new Map();
        this.apiKeys = {
            census: process.env.REACT_APP_CENSUS_API_KEY || 'YOUR_CENSUS_API_KEY',
            fred: process.env.REACT_APP_FRED_API_KEY || 'YOUR_FRED_API_KEY'
        };
        this.baseUrls = {
            census: 'https://api.census.gov/data',
            fred: 'https://api.stlouisfed.org/fred',
            dol: 'https://www.dol.gov/agencies/whd/minimum-wage/state'
        };
        
        // Store real data for easy access
        this.realData = REAL_DATA;
        
        this.dataSources = {
            // === ECONOMICS ===
            medianIncome: {
                source: 'census',
                endpoint: '/2022/acs/acs5',
                variable: 'B19013_001E',
                geographic: 'state:*',
                description: 'Median Household Income',
                category: 'Economics',
                unit: 'dollars'
            },
            medianFamilyIncome: {
                source: 'census',
                endpoint: '/2022/acs/acs5',
                variable: 'B19113_001E',
                geographic: 'state:*',
                description: 'Median Family Income',
                category: 'Economics',
                unit: 'dollars'
            },
            povertyRate: {
                source: 'census',
                endpoint: '/2022/acs/acs5',
                variable: 'B17001_002E',
                geographic: 'state:*',
                description: 'Population Below Poverty Level',
                category: 'Economics',
                unit: 'percentage'
            },
            unemployment: {
                source: 'fred',
                stateMapping: true,
                description: 'Unemployment Rate',
                category: 'Economics',
                unit: 'percentage'
            },
            minWage: {
                source: 'static',
                description: 'State Minimum Wage',
                category: 'Economics',
                unit: 'dollars per hour',
                data: {
                    'AL': 7.25, 'AK': 11.73, 'AZ': 14.35, 'AR': 11.00, 'CA': 16.00, 'CO': 14.42, 'CT': 15.69, 'DE': 13.25,
                    'FL': 12.00, 'GA': 7.25, 'HI': 14.00, 'ID': 7.25, 'IL': 14.00, 'IN': 7.25, 'IA': 7.25, 'KS': 7.25,
                    'KY': 7.25, 'LA': 7.25, 'ME': 14.15, 'MD': 15.00, 'MA': 15.00, 'MI': 10.33, 'MN': 10.85, 'MS': 7.25,
                    'MO': 12.30, 'MT': 10.30, 'NE': 12.00, 'NV': 12.00, 'NH': 7.25, 'NJ': 15.13, 'NM': 12.00, 'NY': 15.00,
                    'NC': 7.25, 'ND': 7.25, 'OH': 10.45, 'OK': 7.25, 'OR': 14.20, 'PA': 7.25, 'RI': 14.00, 'SC': 7.25,
                    'SD': 11.20, 'TN': 7.25, 'TX': 7.25, 'UT': 7.25, 'VT': 13.18, 'VA': 12.00, 'WA': 16.28, 'WV': 8.75,
                    'WI': 7.25, 'WY': 7.25
                }
            },
            stateSalesTax: {
                source: 'static',
                description: 'State Sales Tax Rate',
                category: 'Economics',
                unit: 'percentage',
                data: {
                    'AL': 4.0, 'AK': 0.0, 'AZ': 5.6, 'AR': 6.5, 'CA': 7.5, 'CO': 2.9, 'CT': 6.35, 'DE': 0.0, 'FL': 6.0,
                    'GA': 4.0, 'HI': 4.17, 'ID': 6.0, 'IL': 6.25, 'IN': 7.0, 'IA': 6.0, 'KS': 6.5, 'KY': 6.0, 'LA': 4.45,
                    'ME': 5.5, 'MD': 6.0, 'MA': 6.25, 'MI': 6.0, 'MN': 6.88, 'MS': 7.0, 'MO': 4.23, 'MT': 0.0, 'NE': 5.5,
                    'NV': 6.85, 'NH': 0.0, 'NJ': 6.63, 'NM': 5.13, 'NY': 4.0, 'NC': 4.75, 'ND': 5.0, 'OH': 5.75, 'OK': 4.5,
                    'OR': 0.0, 'PA': 6.0, 'RI': 7.0, 'SC': 6.0, 'SD': 4.2, 'TN': 7.0, 'TX': 6.25, 'UT': 6.1, 'VT': 6.0,
                    'VA': 5.3, 'WA': 6.5, 'WV': 6.0, 'WI': 5.0, 'WY': 4.0
                }
            },
            stateIncomeTax: {
                source: 'static',
                description: 'Top State Income Tax Rate',
                category: 'Economics',
                unit: 'percentage',
                data: {
                    'AL': 5.0, 'AK': 0.0, 'AZ': 4.5, 'AR': 5.5, 'CA': 13.3, 'CO': 4.4, 'CT': 6.99, 'DE': 6.6, 'FL': 0.0,
                    'GA': 5.75, 'HI': 11.0, 'ID': 5.8, 'IL': 4.95, 'IN': 3.23, 'IA': 8.53, 'KS': 5.7, 'KY': 5.0, 'LA': 6.0,
                    'ME': 7.15, 'MD': 5.75, 'MA': 5.0, 'MI': 4.25, 'MN': 9.85, 'MS': 5.0, 'MO': 5.3, 'MT': 6.75, 'NE': 6.84,
                    'NV': 0.0, 'NH': 0.0, 'NJ': 10.75, 'NM': 5.9, 'NY': 10.9, 'NC': 5.25, 'ND': 2.9, 'OH': 3.99, 'OK': 5.0,
                    'OR': 9.9, 'PA': 3.07, 'RI': 5.99, 'SC': 7.0, 'SD': 0.0, 'TN': 0.0, 'TX': 0.0, 'UT': 4.85, 'VT': 8.75,
                    'VA': 5.75, 'WA': 0.0, 'WV': 6.5, 'WI': 7.65, 'WY': 0.0
                }
            },
            gdpPerCapita: {
                source: 'static',
                description: 'GDP Per Capita',
                category: 'Economics',
                unit: 'dollars',
                data: {
                    'AL': 54127, 'AK': 77477, 'AZ': 58570, 'AR': 50054, 'CA': 89540, 'CO': 80566, 'CT': 85596, 'DE': 78468, 'FL': 59227,
                    'GA': 61497, 'HI': 67857, 'ID': 53118, 'IL': 71676, 'IN': 60727, 'IA': 67555, 'KS': 57422, 'KY': 52295, 'LA': 58792,
                    'ME': 57918, 'MD': 74237, 'MA': 91130, 'MI': 55487, 'MN': 71715, 'MS': 43567, 'MO': 56630, 'MT': 53115, 'NE': 68774,
                    'NV': 60365, 'NH': 71305, 'NJ': 70888, 'NM': 49618, 'NY': 79552, 'NC': 58563, 'ND': 65454, 'OH': 62480, 'OK': 53267,
                    'OR': 67058, 'PA': 66323, 'RI': 59533, 'SC': 53199, 'SD': 63216, 'TN': 59917, 'TX': 67852, 'UT': 62974, 'VT': 56990,
                    'VA': 70267, 'WA': 81704, 'WV': 45180, 'WI': 62977, 'WY': 71132
                }
            },

            // === DEMOGRAPHICS ===
            population: {
                source: 'census',
                endpoint: '/2022/pep/population',
                variable: 'POP_2022',
                geographic: 'state:*',
                description: 'Total Population',
                category: 'Demographics',
                unit: 'people'
            },
            populationDensity: {
                source: 'static',
                description: 'Population Density',
                category: 'Demographics',
                unit: 'people per square mile',
                data: {
                    'AL': 99.2, 'AK': 1.3, 'AZ': 64.9, 'AR': 58.4, 'CA': 253.6, 'CO': 56.4, 'CT': 735.8, 'DE': 504.3, 'FL': 397.2,
                    'GA': 186.6, 'HI': 219.9, 'ID': 23.2, 'IL': 228.0, 'IN': 188.1, 'IA': 57.1, 'KS': 35.9, 'KY': 113.9, 'LA': 107.5,
                    'ME': 43.2, 'MD': 636.0, 'MA': 901.2, 'MI': 177.6, 'MN': 71.5, 'MS': 63.7, 'MO': 89.5, 'MT': 7.7, 'NE': 25.4,
                    'NV': 28.5, 'NH': 153.8, 'NJ': 1263.0, 'NM': 17.4, 'NY': 421.0, 'NC': 218.5, 'ND': 11.0, 'OH': 287.5, 'OK': 57.7,
                    'OR': 44.0, 'PA': 290.5, 'RI': 1061.4, 'SC': 173.3, 'SD': 11.9, 'TN': 167.2, 'TX': 114.6, 'UT': 39.9, 'VT': 68.1,
                    'VA': 218.4, 'WA': 117.4, 'WV': 74.6, 'WI': 108.1, 'WY': 5.8
                }
            },
            medianAge: {
                source: 'census',
                endpoint: '/2022/acs/acs5',
                variable: 'B01002_001E',
                geographic: 'state:*',
                description: 'Median Age',
                category: 'Demographics',
                unit: 'years'
            },
            birthRate: {
                source: 'static',
                description: 'Birth Rate',
                category: 'Demographics',
                unit: 'births per 1000 people',
                data: {
                    'AL': 11.8, 'AK': 13.9, 'AZ': 11.6, 'AR': 11.2, 'CA': 11.1, 'CO': 10.8, 'CT': 9.8, 'DE': 10.9, 'FL': 10.2,
                    'GA': 12.4, 'HI': 11.2, 'ID': 12.0, 'IL': 11.0, 'IN': 11.8, 'IA': 11.7, 'KS': 12.1, 'KY': 11.0, 'LA': 12.6,
                    'ME': 9.2, 'MD': 10.8, 'MA': 9.7, 'MI': 10.6, 'MN': 11.0, 'MS': 12.8, 'MO': 11.2, 'MT': 10.8, 'NE': 12.5,
                    'NV': 11.3, 'NH': 9.1, 'NJ': 10.4, 'NM': 11.4, 'NY': 10.5, 'NC': 11.1, 'ND': 13.2, 'OH': 10.9, 'OK': 12.2,
                    'OR': 10.2, 'PA': 10.0, 'RI': 9.8, 'SC': 11.0, 'SD': 12.7, 'TN': 11.1, 'TX': 13.0, 'UT': 14.2, 'VT': 8.8,
                    'VA': 11.0, 'WA': 10.8, 'WV': 10.4, 'WI': 10.8, 'WY': 12.0
                }
            },
            diversityIndex: {
                source: 'static',
                description: 'Racial Diversity Index',
                category: 'Demographics',
                unit: 'index (0-100)',
                data: {
                    'AL': 47.5, 'AK': 62.1, 'AZ': 58.3, 'AR': 40.6, 'CA': 69.7, 'CO': 53.8, 'CT': 62.3, 'DE': 56.8, 'FL': 63.5,
                    'GA': 58.8, 'HI': 76.1, 'ID': 25.5, 'IL': 61.3, 'IN': 37.4, 'IA': 29.8, 'KS': 44.1, 'KY': 24.7, 'LA': 60.1,
                    'ME': 13.5, 'MD': 64.5, 'MA': 47.1, 'MI': 48.4, 'MN': 41.3, 'MS': 55.4, 'MO': 38.0, 'MT': 24.6, 'NE': 40.1,
                    'NV': 64.0, 'NH': 15.3, 'NJ': 68.6, 'NM': 61.9, 'NY': 68.7, 'NC': 54.0, 'ND': 31.4, 'OH': 41.9, 'OK': 54.5,
                    'OR': 43.8, 'PA': 45.8, 'RI': 44.1, 'SC': 52.0, 'SD': 33.4, 'TN': 42.5, 'TX': 69.8, 'UT': 36.2, 'VT': 12.4,
                    'VA': 58.6, 'WA': 53.1, 'WV': 13.6, 'WI': 34.5, 'WY': 26.8
                }
            },

            // === EDUCATION ===
            highSchoolGradRate: {
                source: 'census',
                endpoint: '/2022/acs/acs5',
                variable: 'B15003_017E',
                geographic: 'state:*',
                description: 'High School Graduation Rate',
                category: 'Education',
                unit: 'percentage'
            },
            bachelorsDegreeRate: {
                source: 'census',
                endpoint: '/2022/acs/acs5',
                variable: 'B15003_022E',
                geographic: 'state:*',
                description: 'Bachelor\'s Degree Attainment',
                category: 'Education',
                unit: 'percentage'
            },
            pupilTeacherRatio: {
                source: 'static',
                description: 'Student-Teacher Ratio',
                category: 'Education',
                unit: 'students per teacher',
                data: {
                    'AL': 17.1, 'AK': 16.7, 'AZ': 19.2, 'AR': 14.4, 'CA': 22.9, 'CO': 17.3, 'CT': 12.7, 'DE': 14.9, 'FL': 15.3,
                    'GA': 15.6, 'HI': 15.5, 'ID': 18.8, 'IL': 16.7, 'IN': 17.3, 'IA': 14.1, 'KS': 14.0, 'KY': 15.8, 'LA': 16.2,
                    'ME': 11.7, 'MD': 15.1, 'MA': 13.1, 'MI': 17.6, 'MN': 15.9, 'MS': 15.5, 'MO': 13.8, 'MT': 13.5, 'NE': 14.4,
                    'NV': 18.3, 'NH': 12.0, 'NJ': 12.1, 'NM': 15.1, 'NY': 12.7, 'NC': 15.3, 'ND': 12.3, 'OH': 16.6, 'OK': 15.3,
                    'OR': 21.5, 'PA': 14.8, 'RI': 13.0, 'SC': 15.2, 'SD': 13.8, 'TN': 15.1, 'TX': 15.7, 'UT': 22.9, 'VT': 10.5,
                    'VA': 14.2, 'WA': 18.8, 'WV': 14.0, 'WI': 14.7, 'WY': 12.7
                }
            },
            perPupilSpending: {
                source: 'static',
                description: 'Per Pupil Education Spending',
                category: 'Education',
                unit: 'dollars',
                data: {
                    'AL': 10513, 'AK': 18175, 'AZ': 8770, 'AR': 11432, 'CA': 13072, 'CO': 11141, 'CT': 20635, 'DE': 16395, 'FL': 9346,
                    'GA': 10893, 'HI': 15242, 'ID': 8505, 'IL': 15741, 'IN': 10695, 'IA': 12165, 'KS': 10804, 'KY': 11110, 'LA': 11038,
                    'ME': 16068, 'MD': 15175, 'MA': 17058, 'MI': 12345, 'MN': 13234, 'MS': 9066, 'MO': 11152, 'MT': 12273, 'NE': 13456,
                    'NV': 9687, 'NH': 16893, 'NJ': 20021, 'NM': 10893, 'NY': 24040, 'NC': 9377, 'ND': 14768, 'OH': 12424, 'OK': 8239,
                    'OR': 12300, 'PA': 17170, 'RI': 16849, 'SC': 11227, 'SD': 10143, 'TN': 9544, 'TX': 10342, 'UT': 7628, 'VT': 19340,
                    'VA': 12216, 'WA': 13424, 'WV': 12346, 'WI': 12285, 'WY': 16448
                }
            },
            collegeAffordabilityRank: {
                source: 'static',
                description: 'College Affordability Ranking',
                category: 'Education',
                unit: 'rank (1-50, lower is better)',
                data: {
                    'AL': 15, 'AK': 45, 'AZ': 25, 'AR': 8, 'CA': 47, 'CO': 38, 'CT': 49, 'DE': 42, 'FL': 18,
                    'GA': 22, 'HI': 50, 'ID': 12, 'IL': 35, 'IN': 14, 'IA': 9, 'KS': 13, 'KY': 7, 'LA': 16,
                    'ME': 40, 'MD': 44, 'MA': 48, 'MI': 28, 'MN': 33, 'MS': 4, 'MO': 11, 'MT': 19, 'NE': 17,
                    'NV': 31, 'NH': 46, 'NJ': 43, 'NM': 5, 'NY': 41, 'NC': 21, 'ND': 6, 'OH': 24, 'OK': 10,
                    'OR': 36, 'PA': 37, 'RI': 39, 'SC': 26, 'SD': 3, 'TN': 20, 'TX': 23, 'UT': 1, 'VT': 34,
                    'VA': 32, 'WA': 29, 'WV': 2, 'WI': 27, 'WY': 30
                }
            },

            // === HEALTH ===
            lifeExpectancy: {
                source: 'static',
                description: 'Life Expectancy',
                category: 'Health',
                unit: 'years',
                data: {
                    'AL': 75.4, 'AK': 78.3, 'AZ': 79.2, 'AR': 76.0, 'CA': 80.8, 'CO': 80.2, 'CT': 80.8, 'DE': 78.4, 'FL': 79.4,
                    'GA': 77.2, 'HI': 81.3, 'ID': 79.0, 'IL': 78.1, 'IN': 77.6, 'IA': 79.7, 'KS': 78.7, 'KY': 75.6, 'LA': 76.1,
                    'ME': 79.2, 'MD': 79.0, 'MA': 80.4, 'MI': 77.7, 'MN': 80.8, 'MS': 74.9, 'MO': 77.9, 'MT': 78.8, 'NE': 79.8,
                    'NV': 78.1, 'NH': 80.3, 'NJ': 79.8, 'NM': 78.4, 'NY': 80.5, 'NC': 77.8, 'ND': 79.1, 'OH': 77.8, 'OK': 76.3,
                    'OR': 79.5, 'PA': 78.5, 'RI': 79.9, 'SC': 77.8, 'SD': 79.9, 'TN': 76.3, 'TX': 78.5, 'UT': 80.2, 'VT': 80.5,
                    'VA': 79.0, 'WA': 79.9, 'WV': 75.2, 'WI': 79.2, 'WY': 78.3
                }
            },
            obesityRate: {
                source: 'static',
                description: 'Adult Obesity Rate',
                category: 'Health',
                unit: 'percentage',
                data: {
                    'AL': 36.3, 'AK': 29.8, 'AZ': 28.9, 'AR': 37.4, 'CA': 25.1, 'CO': 23.8, 'CT': 26.3, 'DE': 31.1, 'FL': 28.4,
                    'GA': 33.0, 'HI': 23.8, 'ID': 28.9, 'IL': 32.3, 'IN': 34.8, 'IA': 36.0, 'KS': 33.2, 'KY': 36.6, 'LA': 36.8,
                    'ME': 29.2, 'MD': 30.1, 'MA': 24.3, 'MI': 31.2, 'MN': 27.6, 'MS': 39.7, 'MO': 34.2, 'MT': 25.1, 'NE': 32.4,
                    'NV': 26.7, 'NH': 27.4, 'NJ': 26.9, 'NM': 28.4, 'NY': 27.1, 'NC': 32.1, 'ND': 32.2, 'OH': 32.8, 'OK': 36.5,
                    'OR': 28.7, 'PA': 30.3, 'RI': 27.3, 'SC': 32.3, 'SD': 31.4, 'TN': 36.5, 'TX': 33.0, 'UT': 25.0, 'VT': 26.6,
                    'VA': 29.2, 'WA': 27.7, 'WV': 39.1, 'WI': 31.0, 'WY': 27.4
                }
            },
            smokingRate: {
                source: 'static',
                description: 'Adult Smoking Rate',
                category: 'Health',
                unit: 'percentage',
                data: {
                    'AL': 20.9, 'AK': 21.0, 'AZ': 15.6, 'AR': 22.3, 'CA': 11.3, 'CO': 14.6, 'CT': 12.7, 'DE': 17.0, 'FL': 16.1,
                    'GA': 17.5, 'HI': 14.1, 'ID': 14.3, 'IL': 15.5, 'IN': 21.1, 'IA': 17.1, 'KS': 17.4, 'KY': 23.6, 'LA': 23.1,
                    'ME': 19.0, 'MD': 13.8, 'MA': 13.7, 'MI': 19.3, 'MN': 14.5, 'MS': 22.8, 'MO': 20.8, 'MT': 17.2, 'NE': 15.4,
                    'NV': 17.6, 'NH': 15.7, 'NJ': 13.7, 'NM': 17.5, 'NY': 14.1, 'NC': 17.2, 'ND': 18.3, 'OH': 21.1, 'OK': 20.1,
                    'OR': 16.1, 'PA': 18.7, 'RI': 14.9, 'SC': 18.8, 'SD': 19.3, 'TN': 22.6, 'TX': 15.7, 'UT': 8.9, 'VT': 16.1,
                    'VA': 16.4, 'WA': 13.5, 'WV': 26.0, 'WI': 16.9, 'WY': 18.4
                }
            },
            infantMortalityRate: {
                source: 'static',
                description: 'Infant Mortality Rate',
                category: 'Health',
                unit: 'deaths per 1000 births',
                data: {
                    'AL': 8.4, 'AK': 4.6, 'AZ': 5.7, 'AR': 8.2, 'CA': 4.2, 'CO': 4.5, 'CT': 4.9, 'DE': 7.8, 'FL': 6.0,
                    'GA': 7.2, 'HI': 5.6, 'ID': 5.0, 'IL': 6.4, 'IN': 7.3, 'IA': 4.9, 'KS': 6.7, 'KY': 6.4, 'LA': 8.8,
                    'ME': 5.8, 'MD': 6.8, 'MA': 3.7, 'MI': 7.2, 'MN': 4.9, 'MS': 9.6, 'MO': 6.1, 'MT': 5.5, 'NE': 5.0,
                    'NV': 5.5, 'NH': 4.2, 'NJ': 4.7, 'NM': 5.5, 'NY': 4.6, 'NC': 7.3, 'ND': 6.6, 'OH': 7.2, 'OK': 8.0,
                    'OR': 4.9, 'PA': 6.2, 'RI': 6.4, 'SC': 7.4, 'SD': 7.1, 'TN': 7.3, 'TX': 5.8, 'UT': 5.2, 'VT': 4.4,
                    'VA': 6.0, 'WA': 4.8, 'WV': 7.3, 'WI': 6.0, 'WY': 5.4
                }
            },
            healthcareQualityRank: {
                source: 'static',
                description: 'Healthcare Quality Ranking',
                category: 'Health',
                unit: 'rank (1-50, lower is better)',
                data: {
                    'AL': 46, 'AK': 44, 'AZ': 32, 'AR': 48, 'CA': 19, 'CO': 17, 'CT': 7, 'DE': 18, 'FL': 30,
                    'GA': 40, 'HI': 1, 'ID': 29, 'IL': 22, 'IN': 37, 'IA': 20, 'KS': 31, 'KY': 43, 'LA': 50,
                    'ME': 16, 'MD': 11, 'MA': 3, 'MI': 34, 'MN': 4, 'MS': 49, 'MO': 36, 'MT': 26, 'NE': 21,
                    'NV': 42, 'NH': 9, 'NJ': 8, 'NM': 41, 'NY': 12, 'NC': 35, 'ND': 25, 'OH': 38, 'OK': 47,
                    'OR': 23, 'PA': 27, 'RI': 13, 'SC': 39, 'SD': 28, 'TN': 45, 'TX': 33, 'UT': 5, 'VT': 2,
                    'VA': 14, 'WA': 6, 'WV': 45, 'WI': 15, 'WY': 24
                }
            },

            // === HOUSING & REAL ESTATE ===
            homeValue: {
                source: 'census',
                endpoint: '/2022/acs/acs5',
                variable: 'B25077_001E',
                geographic: 'state:*',
                description: 'Median Home Value',
                category: 'Housing & Real Estate',
                unit: 'dollars'
            },
            medianRent: {
                source: 'census',
                endpoint: '/2022/acs/acs5',
                variable: 'B25064_001E',
                geographic: 'state:*',
                description: 'Median Gross Rent',
                category: 'Housing & Real Estate',
                unit: 'dollars per month'
            },
            homeOwnershipRate: {
                source: 'static',
                description: 'Homeownership Rate',
                category: 'Housing & Real Estate',
                unit: 'percentage',
                data: {
                    'AL': 70.1, 'AK': 64.3, 'AZ': 66.2, 'AR': 66.9, 'CA': 55.8, 'CO': 65.1, 'CT': 67.5, 'DE': 72.4, 'FL': 66.5,
                    'GA': 64.0, 'HI': 58.0, 'ID': 70.0, 'IL': 66.6, 'IN': 69.6, 'IA': 71.2, 'KS': 66.9, 'KY': 68.7, 'LA': 67.1,
                    'ME': 73.0, 'MD': 68.1, 'MA': 62.3, 'MI': 71.4, 'MN': 72.8, 'MS': 69.1, 'MO': 68.4, 'MT': 68.8, 'NE': 67.3,
                    'NV': 56.8, 'NH': 71.0, 'NJ': 64.4, 'NM': 68.6, 'NY': 54.3, 'NC': 65.6, 'ND': 63.6, 'OH': 68.0, 'OK': 67.2,
                    'OR': 62.1, 'PA': 70.1, 'RI': 60.0, 'SC': 70.1, 'SD': 68.2, 'TN': 67.3, 'TX': 62.0, 'UT': 70.9, 'VT': 71.3,
                    'VA': 67.2, 'WA': 63.8, 'WV': 73.4, 'WI': 68.8, 'WY': 71.3
                }
            },
            housingAffordabilityIndex: {
                source: 'static',
                description: 'Housing Affordability Index',
                category: 'Housing & Real Estate',
                unit: 'index (higher is more affordable)',
                data: {
                    'AL': 145.2, 'AK': 87.3, 'AZ': 89.1, 'AR': 142.8, 'CA': 56.2, 'CO': 89.4, 'CT': 102.3, 'DE': 118.5, 'FL': 98.7,
                    'GA': 125.3, 'HI': 52.1, 'ID': 98.6, 'IL': 128.9, 'IN': 145.7, 'IA': 158.9, 'KS': 149.2, 'KY': 152.3, 'LA': 138.7,
                    'ME': 101.5, 'MD': 98.2, 'MA': 88.9, 'MI': 132.1, 'MN': 118.7, 'MS': 149.8, 'MO': 142.1, 'MT': 92.4, 'NE': 132.6,
                    'NV': 84.3, 'NH': 91.2, 'NJ': 78.9, 'NM': 124.5, 'NY': 78.1, 'NC': 118.9, 'ND': 129.8, 'OH': 146.2, 'OK': 136.8,
                    'OR': 87.3, 'PA': 129.4, 'RI': 89.7, 'SC': 128.7, 'SD': 142.3, 'TN': 132.4, 'TX': 114.5, 'UT': 101.2, 'VT': 97.8,
                    'VA': 112.3, 'WA': 78.4, 'WV': 167.8, 'WI': 134.2, 'WY': 118.9
                }
            },
            vacancyRate: {
                source: 'static',
                description: 'Housing Vacancy Rate',
                category: 'Housing & Real Estate',
                unit: 'percentage',
                data: {
                    'AL': 15.2, 'AK': 11.8, 'AZ': 12.9, 'AR': 14.5, 'CA': 7.2, 'CO': 8.9, 'CT': 9.8, 'DE': 15.4, 'FL': 17.3,
                    'GA': 13.1, 'HI': 11.2, 'ID': 9.1, 'IL': 11.5, 'IN': 11.8, 'IA': 9.7, 'KS': 11.2, 'KY': 13.8, 'LA': 14.9,
                    'ME': 18.7, 'MD': 10.4, 'MA': 8.9, 'MI': 12.4, 'MN': 8.1, 'MS': 16.8, 'MO': 12.7, 'MT': 14.2, 'NE': 9.8,
                    'NV': 11.5, 'NH': 12.8, 'NJ': 9.1, 'NM': 15.8, 'NY': 10.8, 'NC': 13.6, 'ND': 8.9, 'OH': 12.1, 'OK': 13.4,
                    'OR': 8.7, 'PA': 11.2, 'RI': 11.9, 'SC': 17.8, 'SD': 11.7, 'TN': 13.2, 'TX': 10.1, 'UT': 7.8, 'VT': 18.9,
                    'VA': 11.5, 'WA': 8.4, 'WV': 17.6, 'WI': 11.1, 'WY': 12.3
                }
            },

            // === ENVIRONMENT ===
            avgTemperature: {
                source: 'static',
                description: 'Average Annual Temperature',
                category: 'Environment',
                unit: 'degrees Fahrenheit',
                data: {
                    'AL': 62.8, 'AK': 26.6, 'AZ': 60.3, 'AR': 60.4, 'CA': 59.4, 'CO': 45.1, 'CT': 49.0, 'DE': 55.3, 'FL': 70.7,
                    'GA': 63.5, 'HI': 70.0, 'ID': 44.4, 'IL': 51.8, 'IN': 51.7, 'IA': 47.8, 'KS': 54.3, 'KY': 55.6, 'LA': 66.4,
                    'ME': 41.0, 'MD': 54.2, 'MA': 47.9, 'MI': 44.4, 'MN': 41.2, 'MS': 63.4, 'MO': 54.5, 'MT': 42.7, 'NE': 48.8,
                    'NV': 49.9, 'NH': 43.8, 'NJ': 52.7, 'NM': 53.4, 'NY': 45.4, 'NC': 59.0, 'ND': 40.4, 'OH': 50.7, 'OK': 59.6,
                    'OR': 48.4, 'PA': 48.8, 'RI': 50.1, 'SC': 62.4, 'SD': 45.2, 'TN': 57.6, 'TX': 64.8, 'UT': 48.6, 'VT': 42.9,
                    'VA': 55.1, 'WA': 48.3, 'WV': 51.8, 'WI': 43.1, 'WY': 42.0
                }
            },
            airQualityIndex: {
                source: 'static',
                description: 'Air Quality Index',
                category: 'Environment',
                unit: 'AQI (lower is better)',
                data: {
                    'AL': 45.2, 'AK': 32.1, 'AZ': 52.8, 'AR': 41.3, 'CA': 63.7, 'CO': 48.9, 'CT': 44.6, 'DE': 47.8, 'FL': 42.1,
                    'GA': 46.9, 'HI': 28.5, 'ID': 39.7, 'IL': 51.4, 'IN': 52.9, 'IA': 43.8, 'KS': 44.2, 'KY': 48.7, 'LA': 47.3,
                    'ME': 36.4, 'MD': 49.1, 'MA': 42.8, 'MI': 47.2, 'MN': 41.9, 'MS': 43.7, 'MO': 48.4, 'MT': 41.8, 'NE': 42.6,
                    'NV': 51.2, 'NH': 37.9, 'NJ': 52.3, 'NM': 46.1, 'NY': 48.6, 'NC': 44.8, 'ND': 38.9, 'OH': 51.7, 'OK': 45.8,
                    'OR': 44.3, 'PA': 50.9, 'RI': 41.2, 'SC': 43.1, 'SD': 39.4, 'TN': 47.5, 'TX': 49.7, 'UT': 52.4, 'VT': 35.1,
                    'VA': 46.2, 'WA': 47.8, 'WV': 49.3, 'WI': 44.7, 'WY': 43.2
                }
            },
            renewableEnergyPercent: {
                source: 'static',
                description: 'Renewable Energy Percentage',
                category: 'Environment',
                unit: 'percentage of total energy',
                data: {
                    'AL': 11.2, 'AK': 23.4, 'AZ': 13.8, 'AR': 14.7, 'CA': 45.8, 'CO': 29.2, 'CT': 8.9, 'DE': 7.1, 'FL': 4.2,
                    'GA': 9.8, 'HI': 35.1, 'ID': 83.7, 'IL': 11.4, 'IN': 7.8, 'IA': 62.1, 'KS': 43.2, 'KY': 6.9, 'LA': 3.4,
                    'ME': 78.9, 'MD': 12.3, 'MA': 15.7, 'MI': 12.1, 'MN': 26.8, 'MS': 2.9, 'MO': 9.7, 'MT': 47.3, 'NE': 23.4,
                    'NV': 28.1, 'NH': 19.8, 'NJ': 5.8, 'NM': 23.7, 'NY': 28.9, 'NC': 7.2, 'ND': 25.8, 'OH': 3.9, 'OK': 35.7,
                    'OR': 74.2, 'PA': 5.6, 'RI': 11.4, 'SC': 8.1, 'SD': 82.1, 'TN': 15.3, 'TX': 23.2, 'UT': 24.9, 'VT': 99.9,
                    'VA': 8.7, 'WA': 89.1, 'WV': 4.1, 'WI': 11.2, 'WY': 9.8
                }
            },
            waterQualityViolations: {
                source: 'static',
                description: 'Water Quality Violations',
                category: 'Environment',
                unit: 'violations per 1000 people',
                data: {
                    'AL': 2.8, 'AK': 12.4, 'AZ': 1.9, 'AR': 5.7, 'CA': 1.2, 'CO': 3.4, 'CT': 0.8, 'DE': 1.6, 'FL': 2.1,
                    'GA': 3.2, 'HI': 4.9, 'ID': 8.1, 'IL': 2.4, 'IN': 3.8, 'IA': 6.2, 'KS': 5.9, 'KY': 4.7, 'LA': 3.9,
                    'ME': 7.8, 'MD': 1.4, 'MA': 0.9, 'MI': 4.2, 'MN': 2.7, 'MS': 4.1, 'MO': 4.8, 'MT': 11.2, 'NE': 7.4,
                    'NV': 2.3, 'NH': 5.6, 'NJ': 1.1, 'NM': 6.8, 'NY': 1.7, 'NC': 2.9, 'ND': 8.7, 'OH': 3.1, 'OK': 6.3,
                    'OR': 3.7, 'PA': 2.6, 'RI': 1.3, 'SC': 3.6, 'SD': 9.1, 'TN': 3.4, 'TX': 2.8, 'UT': 4.2, 'VT': 6.9,
                    'VA': 2.1, 'WA': 2.9, 'WV': 5.8, 'WI': 3.9, 'WY': 8.4
                }
            },
            carbonFootprint: {
                source: 'static',
                description: 'CO2 Emissions Per Capita',
                category: 'Environment',
                unit: 'metric tons per person',
                data: {
                    'AL': 25.8, 'AK': 22.1, 'AZ': 11.2, 'AR': 22.9, 'CA': 9.3, 'CO': 16.4, 'CT': 7.8, 'DE': 16.2, 'FL': 10.1,
                    'GA': 15.7, 'HI': 14.3, 'ID': 15.8, 'IL': 12.4, 'IN': 21.7, 'IA': 19.2, 'KS': 18.9, 'KY': 26.5, 'LA': 24.1,
                    'ME': 12.3, 'MD': 9.8, 'MA': 8.1, 'MI': 14.6, 'MN': 13.7, 'MS': 18.4, 'MO': 17.2, 'MT': 16.9, 'NE': 20.1,
                    'NV': 13.8, 'NH': 9.4, 'NJ': 9.2, 'NM': 17.6, 'NY': 8.2, 'NC': 12.8, 'ND': 48.4, 'OH': 15.8, 'OK': 16.7,
                    'OR': 8.9, 'PA': 13.2, 'RI': 7.9, 'SC': 13.4, 'SD': 14.2, 'TN': 14.1, 'TX': 22.5, 'UT': 15.9, 'VT': 6.8,
                    'VA': 11.6, 'WA': 9.7, 'WV': 38.8, 'WI': 13.9, 'WY': 69.2
                }
            },

            // === CRIME & SAFETY ===
            violentCrimeRate: {
                source: 'static',
                description: 'Violent Crime Rate',
                category: 'Crime & Safety',
                unit: 'incidents per 100,000 people',
                data: {
                    'AL': 453.6, 'AK': 837.8, 'AZ': 455.3, 'AR': 671.9, 'CA': 441.2, 'CO': 423.1, 'CT': 183.6, 'DE': 422.6, 'FL': 378.4,
                    'GA': 400.1, 'HI': 254.2, 'ID': 242.6, 'IL': 425.9, 'IN': 382.3, 'IA': 266.6, 'KS': 425.0, 'KY': 259.1, 'LA': 639.4,
                    'ME': 115.2, 'MD': 468.7, 'MA': 308.8, 'MI': 478.0, 'MN': 220.4, 'MS': 291.2, 'MO': 542.7, 'MT': 469.8, 'NE': 291.0,
                    'NV': 460.3, 'NH': 146.4, 'NJ': 195.4, 'NM': 778.3, 'NY': 363.8, 'NC': 419.3, 'ND': 280.4, 'OH': 293.2, 'OK': 458.6,
                    'OR': 291.9, 'PA': 306.0, 'RI': 230.8, 'SC': 530.7, 'SD': 404.7, 'TN': 672.7, 'TX': 446.5, 'UT': 236.8, 'VT': 173.4,
                    'VA': 208.7, 'WA': 293.7, 'WV': 316.6, 'WI': 295.4, 'WY': 212.2
                }
            },
            propertyyCrimeRate: {
                source: 'static',
                description: 'Property Crime Rate',
                category: 'Crime & Safety',
                unit: 'incidents per 100,000 people',
                data: {
                    'AL': 2388.4, 'AK': 2992.8, 'AZ': 2489.2, 'AR': 3062.6, 'CA': 2331.2, 'CO': 3039.0, 'CT': 1610.5, 'DE': 2301.4, 'FL': 1909.4,
                    'GA': 2316.1, 'HI': 2992.7, 'ID': 1346.5, 'IL': 1983.9, 'IN': 2034.8, 'IA': 1691.1, 'KS': 2496.1, 'KY': 1663.8, 'LA': 3297.6,
                    'ME': 1043.8, 'MD': 2027.8, 'MA': 1240.8, 'MI': 1815.6, 'MN': 2154.9, 'MS': 2403.9, 'MO': 2909.4, 'MT': 2119.6, 'NE': 2098.6,
                    'NV': 2838.1, 'NH': 1361.4, 'NJ': 1263.0, 'NM': 3937.9, 'NY': 1443.7, 'NC': 2204.2, 'ND': 1678.4, 'OH': 2034.9, 'OK': 2752.7,
                    'OR': 2891.8, 'PA': 1416.8, 'RI': 1505.1, 'SC': 2621.9, 'SD': 1633.9, 'TN': 2927.1, 'TX': 2515.3, 'UT': 2318.4, 'VT': 1173.7,
                    'VA': 1452.0, 'WA': 3346.2, 'WV': 1318.7, 'WI': 1682.4, 'WY': 1463.2
                }
            },
            prisonIncarceration: {
                source: 'static',
                description: 'Incarceration Rate',
                category: 'Crime & Safety',
                unit: 'per 100,000 people',
                data: {
                    'AL': 650, 'AK': 280, 'AZ': 590, 'AR': 590, 'CA': 325, 'CO': 370, 'CT': 245, 'DE': 365, 'FL': 430,
                    'GA': 510, 'HI': 240, 'ID': 485, 'IL': 290, 'IN': 415, 'IA': 285, 'KS': 360, 'KY': 510, 'LA': 695,
                    'ME': 140, 'MD': 360, 'MA': 175, 'MI': 385, 'MN': 185, 'MS': 615, 'MO': 480, 'MT': 395, 'NE': 285,
                    'NV': 430, 'NH': 195, 'NJ': 240, 'NM': 310, 'NY': 225, 'NC': 340, 'ND': 235, 'OH': 435, 'OK': 680,
                    'OR': 330, 'PA': 340, 'RI': 185, 'SC': 385, 'SD': 425, 'TN': 455, 'TX': 545, 'UT': 245, 'VT': 155,
                    'VA': 395, 'WA': 265, 'WV': 385, 'WI': 370, 'WY': 385
                }
            },

            // === INFRASTRUCTURE ===
            internetSpeed: {
                source: 'static',
                description: 'Average Internet Speed',
                category: 'Infrastructure',
                unit: 'Mbps',
                data: {
                    'AL': 98.4, 'AK': 67.8, 'AZ': 115.7, 'AR': 89.2, 'CA': 128.9, 'CO': 119.3, 'CT': 134.2, 'DE': 118.5, 'FL': 109.8,
                    'GA': 107.4, 'HI': 87.1, 'ID': 95.3, 'IL': 116.8, 'IN': 102.1, 'IA': 91.7, 'KS': 96.8, 'KY': 89.4, 'LA': 93.7,
                    'ME': 88.9, 'MD': 125.3, 'MA': 141.7, 'MI': 108.2, 'MN': 112.6, 'MS': 83.1, 'MO': 104.7, 'MT': 78.4, 'NE': 98.9,
                    'NV': 111.4, 'NH': 126.8, 'NJ': 132.6, 'NM': 86.7, 'NY': 129.4, 'NC': 113.8, 'ND': 87.3, 'OH': 109.7, 'OK': 92.1,
                    'OR': 117.2, 'PA': 114.3, 'RI': 128.7, 'SC': 101.4, 'SD': 91.2, 'TN': 106.2, 'TX': 118.9, 'UT': 121.4, 'VT': 94.7,
                    'VA': 122.8, 'WA': 124.7, 'WV': 76.2, 'WI': 103.8, 'WY': 81.7
                }
            },
            roadQualityRank: {
                source: 'static',
                description: 'Road Quality Ranking',
                category: 'Infrastructure',
                unit: 'rank (1-50, lower is better)',
                data: {
                    'AL': 38, 'AK': 50, 'AZ': 45, 'AR': 46, 'CA': 49, 'CO': 27, 'CT': 48, 'DE': 25, 'FL': 12,
                    'GA': 1, 'HI': 47, 'ID': 23, 'IL': 44, 'IN': 19, 'IA': 28, 'KS': 16, 'KY': 35, 'LA': 43,
                    'ME': 37, 'MD': 32, 'MA': 42, 'MI': 40, 'MN': 18, 'MS': 39, 'MO': 41, 'MT': 31, 'NE': 15,
                    'NV': 2, 'NH': 3, 'NJ': 36, 'NM': 34, 'NY': 33, 'NC': 9, 'ND': 7, 'OH': 30, 'OK': 26,
                    'OR': 24, 'PA': 29, 'RI': 22, 'SC': 14, 'SD': 11, 'TN': 4, 'TX': 8, 'UT': 13, 'VT': 21,
                    'VA': 5, 'WA': 17, 'WV': 20, 'WI': 10, 'WY': 6
                }
            },
            bridgeCondition: {
                source: 'static',
                description: 'Structurally Deficient Bridges',
                category: 'Infrastructure',
                unit: 'percentage of bridges',
                data: {
                    'AL': 8.2, 'AK': 5.8, 'AZ': 6.1, 'AR': 7.8, 'CA': 7.3, 'CO': 5.9, 'CT': 6.2, 'DE': 4.9, 'FL': 2.5,
                    'GA': 3.8, 'HI': 15.2, 'ID': 4.1, 'IL': 8.7, 'IN': 8.9, 'IA': 19.1, 'KS': 5.1, 'KY': 6.9, 'LA': 12.4,
                    'ME': 9.3, 'MD': 5.7, 'MA': 8.1, 'MI': 10.8, 'MN': 4.2, 'MS': 10.1, 'MO': 8.5, 'MT': 3.2, 'NE': 15.4,
                    'NV': 1.7, 'NH': 11.2, 'NJ': 6.8, 'NM': 2.9, 'NY': 9.7, 'NC': 4.6, 'ND': 7.8, 'OH': 7.4, 'OK': 6.3,
                    'OR': 2.1, 'PA': 16.6, 'RI': 20.8, 'SC': 8.9, 'SD': 15.7, 'TN': 6.2, 'TX': 1.4, 'UT': 3.4, 'VT': 7.8,
                    'VA': 6.1, 'WA': 4.7, 'WV': 21.2, 'WI': 8.3, 'WY': 6.7
                }
            },
            publicTransitScore: {
                source: 'static',
                description: 'Public Transit Accessibility',
                category: 'Infrastructure',
                unit: 'score (0-100, higher is better)',
                data: {
                    'AL': 12, 'AK': 8, 'AZ': 25, 'AR': 10, 'CA': 67, 'CO': 45, 'CT': 52, 'DE': 18, 'FL': 28,
                    'GA': 32, 'HI': 38, 'ID': 12, 'IL': 68, 'IN': 19, 'IA': 14, 'KS': 16, 'KY': 15, 'LA': 22,
                    'ME': 15, 'MD': 56, 'MA': 72, 'MI': 24, 'MN': 48, 'MS': 8, 'MO': 26, 'MT': 11, 'NE': 13,
                    'NV': 29, 'NH': 12, 'NJ': 69, 'NM': 18, 'NY': 85, 'NC': 23, 'ND': 7, 'OH': 31, 'OK': 14,
                    'OR': 42, 'PA': 46, 'RI': 34, 'SC': 16, 'SD': 6, 'TN': 19, 'TX': 34, 'UT': 39, 'VT': 16,
                    'VA': 41, 'WA': 54, 'WV': 9, 'WI': 27, 'WY': 5
                }
            },
            
            // === POLITICS & GOVERNANCE ===
            voterTurnout2020: {
                source: 'static',
                description: '2020 Presidential Election Turnout',
                category: 'Politics & Governance',
                unit: 'percentage of eligible voters',
                data: {
                    'AL': 63.1, 'AK': 58.8, 'AZ': 79.9, 'AR': 54.9, 'CA': 71.0, 'CO': 87.6, 'CT': 75.4, 'DE': 70.1, 'FL': 77.3,
                    'GA': 67.7, 'HI': 59.7, 'ID': 81.0, 'IL': 72.9, 'IN': 67.0, 'IA': 76.0, 'KS': 70.6, 'KY': 59.4, 'LA': 67.9,
                    'ME': 76.4, 'MD': 75.3, 'MA': 76.6, 'MI': 75.7, 'MN': 79.6, 'MS': 59.7, 'MO': 68.7, 'MT': 78.9, 'NE': 69.2,
                    'NV': 77.3, 'NH': 76.3, 'NJ': 71.6, 'NM': 69.6, 'NY': 61.7, 'NC': 75.4, 'ND': 65.6, 'OH': 74.1, 'OK': 57.5,
                    'OR': 82.7, 'PA': 70.9, 'RI': 69.3, 'SC': 68.4, 'SD': 69.3, 'TN': 62.7, 'TX': 66.7, 'UT': 78.3, 'VT': 77.6,
                    'VA': 74.2, 'WA': 84.1, 'WV': 57.4, 'WI': 76.8, 'WY': 69.9
                }
            },
            governmentEfficiency: {
                source: 'static',
                description: 'Government Efficiency Ranking',
                category: 'Politics & Governance',
                unit: 'rank (1-50, lower is better)',
                data: {
                    'AL': 45, 'AK': 49, 'AZ': 23, 'AR': 42, 'CA': 44, 'CO': 12, 'CT': 35, 'DE': 18, 'FL': 19,
                    'GA': 15, 'HI': 50, 'ID': 6, 'IL': 48, 'IN': 13, 'IA': 7, 'KS': 17, 'KY': 39, 'LA': 47,
                    'ME': 26, 'MD': 30, 'MA': 24, 'MI': 38, 'MN': 9, 'MS': 46, 'MO': 32, 'MT': 21, 'NE': 8,
                    'NV': 33, 'NH': 2, 'NJ': 40, 'NM': 43, 'NY': 41, 'NC': 16, 'ND': 3, 'OH': 29, 'OK': 34,
                    'OR': 27, 'PA': 31, 'RI': 37, 'SC': 25, 'SD': 4, 'TN': 14, 'TX': 11, 'UT': 1, 'VT': 22,
                    'VA': 10, 'WA': 5, 'WV': 36, 'WI': 20, 'WY': 28
                }
            },
            corruptionRank: {
                source: 'static',
                description: 'Public Corruption Risk',
                category: 'Politics & Governance',
                unit: 'rank (1-50, lower is better/less corrupt)',
                data: {
                    'AL': 45, 'AK': 15, 'AZ': 34, 'AR': 41, 'CA': 35, 'CO': 7, 'CT': 5, 'DE': 12, 'FL': 39,
                    'GA': 3, 'HI': 23, 'ID': 6, 'IL': 50, 'IN': 24, 'IA': 2, 'KS': 16, 'KY': 42, 'LA': 49,
                    'ME': 8, 'MD': 26, 'MA': 19, 'MI': 32, 'MN': 9, 'MS': 44, 'MO': 36, 'MT': 18, 'NE': 10,
                    'NV': 33, 'NH': 1, 'NJ': 46, 'NM': 40, 'NY': 43, 'NC': 22, 'ND': 11, 'OH': 37, 'OK': 38,
                    'OR': 14, 'PA': 47, 'RI': 48, 'SC': 29, 'SD': 4, 'TN': 31, 'TX': 28, 'UT': 13, 'VT': 17,
                    'VA': 21, 'WA': 20, 'WV': 30, 'WI': 25, 'WY': 27
                }
            },

            // === LEGAL & SOCIAL ===
            marijuana: {
                source: 'static',
                description: 'Marijuana Legal Status',
                category: 'Legal & Social',
                data: {
                    'AL': 'Illegal', 'AK': 'Legal', 'AZ': 'Legal', 'AR': 'Medical only', 'CA': 'Legal', 'CO': 'Legal',
                    'CT': 'Legal', 'DE': 'Medical only', 'FL': 'Medical only', 'GA': 'Illegal', 'HI': 'Medical only',
                    'ID': 'Illegal', 'IL': 'Legal', 'IN': 'Illegal', 'IA': 'Illegal', 'KS': 'Illegal', 'KY': 'Medical only',
                    'LA': 'Medical only', 'ME': 'Legal', 'MD': 'Legal', 'MA': 'Legal', 'MI': 'Legal', 'MN': 'Legal',
                    'MS': 'Medical only', 'MO': 'Legal', 'MT': 'Legal', 'NE': 'Illegal', 'NV': 'Legal', 'NH': 'Medical only',
                    'NJ': 'Legal', 'NM': 'Legal', 'NY': 'Legal', 'NC': 'Illegal', 'ND': 'Medical only', 'OH': 'Legal',
                    'OK': 'Medical only', 'OR': 'Legal', 'PA': 'Medical only', 'RI': 'Legal', 'SC': 'Illegal',
                    'SD': 'Medical only', 'TN': 'Illegal', 'TX': 'Illegal', 'UT': 'Medical only', 'VT': 'Legal',
                    'VA': 'Legal', 'WA': 'Legal', 'WV': 'Medical only', 'WI': 'Illegal', 'WY': 'Illegal'
                }
            },
            ageOfConsent: {
                source: 'static',
                description: 'Age of Consent',
                category: 'Legal & Social',
                unit: 'years',
                data: {
                    'AL': 16, 'AK': 16, 'AZ': 18, 'AR': 16, 'CA': 18, 'CO': 17, 'CT': 16, 'DE': 18, 'FL': 18,
                    'GA': 16, 'HI': 16, 'ID': 18, 'IL': 17, 'IN': 16, 'IA': 16, 'KS': 16, 'KY': 16, 'LA': 17,
                    'ME': 16, 'MD': 16, 'MA': 16, 'MI': 16, 'MN': 16, 'MS': 16, 'MO': 17, 'MT': 16, 'NE': 16,
                    'NV': 16, 'NH': 16, 'NJ': 16, 'NM': 17, 'NY': 17, 'NC': 16, 'ND': 18, 'OH': 16, 'OK': 16,
                    'OR': 18, 'PA': 16, 'RI': 16, 'SC': 16, 'SD': 16, 'TN': 18, 'TX': 17, 'UT': 18, 'VT': 16,
                    'VA': 18, 'WA': 16, 'WV': 16, 'WI': 18, 'WY': 17
                }
            },
            deathPenalty: {
                source: 'static',
                description: 'Death Penalty Status',
                category: 'Legal & Social',
                data: {
                    'AL': 'Legal', 'AK': 'Abolished', 'AZ': 'Legal', 'AR': 'Legal', 'CA': 'Legal (Moratorium)', 'CO': 'Abolished',
                    'CT': 'Abolished', 'DE': 'Abolished', 'FL': 'Legal', 'GA': 'Legal', 'HI': 'Abolished', 'ID': 'Legal',
                    'IL': 'Abolished', 'IN': 'Legal', 'IA': 'Abolished', 'KS': 'Legal', 'KY': 'Legal', 'LA': 'Legal',
                    'ME': 'Abolished', 'MD': 'Abolished', 'MA': 'Abolished', 'MI': 'Abolished', 'MN': 'Abolished',
                    'MS': 'Legal', 'MO': 'Legal', 'MT': 'Legal', 'NE': 'Abolished', 'NV': 'Abolished', 'NH': 'Abolished',
                    'NJ': 'Abolished', 'NM': 'Abolished', 'NY': 'Abolished', 'NC': 'Legal', 'ND': 'Abolished', 'OH': 'Legal',
                    'OK': 'Legal', 'OR': 'Legal (Moratorium)', 'PA': 'Legal (Moratorium)', 'RI': 'Abolished', 'SC': 'Legal',
                    'SD': 'Legal', 'TN': 'Legal', 'TX': 'Legal', 'UT': 'Legal', 'VT': 'Abolished', 'VA': 'Abolished',
                    'WA': 'Abolished', 'WV': 'Abolished', 'WI': 'Abolished', 'WY': 'Legal'
                }
            },
            gunLawStrength: {
                source: 'static',
                description: 'Gun Law Strength Score',
                category: 'Legal & Social',
                unit: 'grade (A+ to F, A+ is strongest)',
                data: {
                    'AL': 'F', 'AK': 'F', 'AZ': 'D-', 'AR': 'F', 'CA': 'A', 'CO': 'B-', 'CT': 'A-', 'DE': 'C+', 'FL': 'C-',
                    'GA': 'F', 'HI': 'A-', 'ID': 'F', 'IL': 'A-', 'IN': 'D', 'IA': 'C-', 'KS': 'F', 'KY': 'F', 'LA': 'F',
                    'ME': 'C+', 'MD': 'A-', 'MA': 'A-', 'MI': 'C-', 'MN': 'B-', 'MS': 'F', 'MO': 'F', 'MT': 'F', 'NE': 'D',
                    'NV': 'B-', 'NH': 'D-', 'NJ': 'A-', 'NM': 'C-', 'NY': 'A-', 'NC': 'D+', 'ND': 'F', 'OH': 'D-', 'OK': 'F',
                    'OR': 'B', 'PA': 'C+', 'RI': 'B+', 'SC': 'F', 'SD': 'F', 'TN': 'F', 'TX': 'F', 'UT': 'D', 'VT': 'C+',
                    'VA': 'C+', 'WA': 'A-', 'WV': 'F', 'WI': 'D+', 'WY': 'F'
                }
            }
        };
    }

    async getData(dataType, forceRefresh = false) {
        const cacheKey = `data_${dataType}`;
        
        if (!forceRefresh && this.isDataCached(cacheKey)) {
            console.log(`Using cached data for ${dataType}`);
            return this.cache.get(cacheKey);
        }

        console.log(`Getting data for ${dataType}`);
        
        // First check if we have real fetched data for this metric
        const realDataMapping = {
            'population': 'population',
            'medianIncome': 'medianHouseholdIncome',
            'medianFamilyIncome': 'medianFamilyIncome',
            'medianHouseholdIncome': 'medianHouseholdIncome',
            'povertyRate': 'povertyRate',
            'unemployment': 'unemploymentRate',
            'unemploymentRate': 'unemploymentRate',
            'minWage': 'minimumWage',
            'minimumWage': 'minimumWage',
            'costOfLiving': 'costOfLivingIndex',
            'costOfLivingIndex': 'costOfLivingIndex',
            'highSchoolGraduation': 'highSchoolGraduation',
            'collegeAttainment': 'educationBachelor',
            'educationBachelor': 'educationBachelor',
            'lifeExpectancy': 'lifeExpectancy',
            'medianHomeValue': 'medianHomeValue',
            'medianRent': 'medianRent',
            'uninsuredRate': 'uninsuredRate',
            'violentCrimeRate': 'violentCrimeRate',
            'propertyCrimeRate': 'propertyCrimeRate',
            'populationDensity': 'populationDensity',
            'homeownershipRate': 'homeownershipRate',
            'medianAge': 'medianAge',
            'gdpPerCapita': 'gdpPerCapita',
            'infantMortalityRate': 'infantMortalityRate',
            'averageCommuteTime': 'averageCommuteTime',
            'stateIncomeTaxRate': 'stateIncomeTaxRate',
            'salesTaxRate': 'salesTaxRate',
            'broadbandAvailability': 'broadbandAvailability',
            'airQualityIndex': 'airQualityIndex',
            'internetSpeed': 'internetSpeed'
        };
        
        // Check if we have real data for this metric
        const realDataKey = realDataMapping[dataType];
        if (realDataKey && this.realData[realDataKey]) {
            const data = this.realData[realDataKey];
            // Check if data is complete (has all states)
            if (Object.keys(data).length >= 40) {
                console.log(`Using real fetched data for ${dataType}`);
                this.cacheData(cacheKey, data);
                return data;
            }
        }
        
        try {
            let data;
            const source = this.dataSources[dataType];
            
            if (!source) {
                // If no source defined but we have real data, return it
                if (realDataKey && this.realData[realDataKey]) {
                    return this.realData[realDataKey];
                }
                throw new Error(`Unknown data type: ${dataType}`);
            }

            switch (source.source) {
                case 'census':
                    // Try real data first, fallback to API
                    if (realDataKey && this.realData[realDataKey]) {
                        data = this.realData[realDataKey];
                    } else {
                        data = await this.fetchCensusData(source);
                    }
                    break;
                case 'fred':
                    // Use real unemployment data if available
                    if (dataType === 'unemployment' && this.realData.unemploymentRate) {
                        data = this.realData.unemploymentRate;
                    } else {
                        data = await this.fetchFredData(source);
                    }
                    break;
                case 'static':
                    // Use updated real data if available
                    if (realDataKey && this.realData[realDataKey]) {
                        data = this.realData[realDataKey];
                    } else {
                        data = source.data;
                    }
                    break;
                default:
                    throw new Error(`Unknown source type: ${source.source}`);
            }

            this.cacheData(cacheKey, data);
            return data;
            
        } catch (error) {
            console.error(`Error fetching ${dataType}:`, error);
            
            if (this.cache.has(cacheKey)) {
                console.log(`Using stale cached data for ${dataType} due to error`);
                return this.cache.get(cacheKey);
            }
            
            throw error;
        }
    }

    async fetchCensusData(source) {
        const url = `${this.baseUrls.census}${source.endpoint}?get=${source.variable},NAME&for=${source.geographic}&key=${this.apiKeys.census}`;
        
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Census API error: ${response.status} ${response.statusText}`);
            }
            
            const data = await response.json();
            
            if (!data || data.length < 2) {
                throw new Error('Invalid Census API response format');
            }
            
            const rows = data.slice(1);
            
            const result = {};
            
            rows.forEach(row => {
                const stateName = row[1];
                const value = parseInt(row[0]);
                const stateCode = this.getStateCode(stateName);
                
                if (stateCode && !isNaN(value)) {
                    result[stateCode] = value;
                }
            });
            
            return result;
            
        } catch (error) {
            console.error('Census API fetch error:', error);
            throw error;
        }
    }

    async fetchFredData(source) {
        const stateData = {};
        const stateCodes = [
            'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
            'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
            'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
            'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
            'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
        ];

        for (const stateCode of stateCodes) {
            try {
                const seriesId = `${stateCode}UR`;
                const url = `${this.baseUrls.fred}/series/observations?series_id=${seriesId}&api_key=${this.apiKeys.fred}&file_type=json&limit=1&sort_order=desc`;
                
                const response = await fetch(url);
                if (response.ok) {
                    const data = await response.json();
                    if (data.observations && data.observations.length > 0) {
                        stateData[stateCode] = parseFloat(data.observations[0].value);
                    }
                }
                
                await this.delay(100);
                
            } catch (error) {
                console.warn(`Failed to fetch FRED data for ${stateCode}:`, error);
            }
        }
        
        return stateData;
    }

    isDataCached(key) {
        if (!this.cache.has(key)) {
            return false;
        }
        
        const expiry = this.cacheExpiry.get(key);
        if (expiry && Date.now() > expiry) {
            this.cache.delete(key);
            this.cacheExpiry.delete(key);
            return false;
        }
        
        return true;
    }

    cacheData(key, data, ttlMinutes = 60) {
        this.cache.set(key, data);
        this.cacheExpiry.set(key, Date.now() + (ttlMinutes * 60 * 1000));
    }

    getStateCode(stateName) {
        const stateMapping = {
            'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
            'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
            'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA', 'Kansas': 'KS',
            'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD', 'Massachusetts': 'MA',
            'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO', 'Montana': 'MT',
            'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM',
            'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH', 'Oklahoma': 'OK',
            'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
            'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
            'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY'
        };
        return stateMapping[stateName];
    }

    getAllStates() {
        // Return all states with their codes and names
        const stateMapping = {
            'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
            'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
            'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa', 'KS': 'Kansas',
            'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland', 'MA': 'Massachusetts',
            'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri', 'MT': 'Montana',
            'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey', 'NM': 'New Mexico',
            'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio', 'OK': 'Oklahoma',
            'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina', 'SD': 'South Dakota',
            'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont', 'VA': 'Virginia',
            'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming'
        };
        
        const states = [];
        Object.keys(stateMapping).forEach(code => {
            states.push({
                code: code,
                name: stateMapping[code]
            });
        });
        return states;
    }

    getStateName(stateCode) {
        const stateMapping = {
            'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
            'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
            'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa', 'KS': 'Kansas',
            'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland', 'MA': 'Massachusetts',
            'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri', 'MT': 'Montana',
            'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey', 'NM': 'New Mexico',
            'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio', 'OK': 'Oklahoma',
            'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
            'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
            'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming'
        };
        return stateMapping[stateCode];
    }

    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    getAllDataTypes() {
        return Object.keys(this.dataSources);
    }

    getDataDescription(dataType) {
        return this.dataSources[dataType]?.description || dataType;
    }

    getDataCategory(dataType) {
        return this.dataSources[dataType]?.category || 'Other';
    }

    getDataTypesByCategory() {
        const categories = {};
        Object.entries(this.dataSources).forEach(([key, value]) => {
            const category = value.category || 'Other';
            if (!categories[category]) {
                categories[category] = [];
            }
            categories[category].push({
                key,
                name: value.description,
                description: value.description,
                unit: value.unit,
                source: value.source
            });
        });
        return categories;
    }

    async refreshAllData() {
        const dataTypes = this.getAllDataTypes();
        const results = {};
        
        for (const dataType of dataTypes) {
            try {
                results[dataType] = await this.getData(dataType, true);
                console.log(`Refreshed ${dataType}: ${Object.keys(results[dataType]).length} states`);
            } catch (error) {
                console.error(`Failed to refresh ${dataType}:`, error);
                results[dataType] = null;
            }
            
            await this.delay(1000);
        }
        
        return results;
    }

    clearCache() {
        this.cache.clear();
        this.cacheExpiry.clear();
        console.log('Data cache cleared');
    }

    getCacheStatus() {
        const status = {};
        for (const [key, expiry] of this.cacheExpiry.entries()) {
            const timeRemaining = expiry - Date.now();
            status[key] = {
                cached: this.cache.has(key),
                expiresIn: Math.max(0, Math.floor(timeRemaining / 1000 / 60)),
                recordCount: this.cache.has(key) ? Object.keys(this.cache.get(key)).length : 0
            };
        }
        return status;
    }

    // Convert FIPS code to state abbreviation
    getStateAbbreviation(fipsCode) {
        const fipsToState = {
            '01': 'AL', '02': 'AK', '04': 'AZ', '05': 'AR', '06': 'CA',
            '08': 'CO', '09': 'CT', '10': 'DE', '11': 'DC', '12': 'FL',
            '13': 'GA', '15': 'HI', '16': 'ID', '17': 'IL', '18': 'IN',
            '19': 'IA', '20': 'KS', '21': 'KY', '22': 'LA', '23': 'ME',
            '24': 'MD', '25': 'MA', '26': 'MI', '27': 'MN', '28': 'MS',
            '29': 'MO', '30': 'MT', '31': 'NE', '32': 'NV', '33': 'NH',
            '34': 'NJ', '35': 'NM', '36': 'NY', '37': 'NC', '38': 'ND',
            '39': 'OH', '40': 'OK', '41': 'OR', '42': 'PA', '44': 'RI',
            '45': 'SC', '46': 'SD', '47': 'TN', '48': 'TX', '49': 'UT',
            '50': 'VT', '51': 'VA', '53': 'WA', '54': 'WV', '55': 'WI',
            '56': 'WY'
        };
        
        // If already a state abbreviation, return as is
        if (fipsCode && fipsCode.length === 2 && isNaN(fipsCode)) {
            return fipsCode;
        }
        
        // Convert FIPS to state
        return fipsToState[fipsCode] || null;
    }
    
    getStateData(stateCode) {
        if (!stateCode) return null;
        
        // Return comprehensive data package for a state
        return {
            // Population & Demographics
            population: this.realData.population[stateCode],
            populationDensity: this.realData.populationDensity[stateCode],
            medianAge: this.realData.medianAge[stateCode],
            
            // Economics
            medianHouseholdIncome: this.realData.medianHouseholdIncome[stateCode],
            medianFamilyIncome: this.realData.medianFamilyIncome[stateCode],
            povertyRate: this.realData.povertyRate[stateCode],
            unemploymentRate: this.realData.unemploymentRate[stateCode],
            minimumWage: this.realData.minimumWage[stateCode],
            gdpPerCapita: this.realData.gdpPerCapita[stateCode],
            
            // Housing
            medianHomeValue: this.realData.medianHomeValue[stateCode],
            medianRent: this.realData.medianRent[stateCode],
            homeownershipRate: this.realData.homeownershipRate[stateCode],
            
            // Education
            educationBachelor: this.realData.educationBachelor[stateCode],
            highSchoolGraduation: this.realData.highSchoolGraduation[stateCode],
            
            // Healthcare
            uninsuredRate: this.realData.uninsuredRate[stateCode],
            lifeExpectancy: this.realData.lifeExpectancy[stateCode],
            infantMortalityRate: this.realData.infantMortalityRate[stateCode],
            
            // Crime & Safety
            violentCrimeRate: this.realData.violentCrimeRate[stateCode],
            propertyCrimeRate: this.realData.propertyCrimeRate[stateCode],
            
            // Infrastructure & Quality of Life
            averageCommuteTime: this.realData.averageCommuteTime[stateCode],
            broadbandAvailability: this.realData.broadbandAvailability[stateCode],
            internetSpeed: this.realData.internetSpeed[stateCode],
            airQualityIndex: this.realData.airQualityIndex[stateCode],
            
            // Business & Taxes
            costOfLivingIndex: this.realData.costOfLivingIndex[stateCode],
            stateIncomeTaxRate: this.realData.stateIncomeTaxRate[stateCode],
            salesTaxRate: this.realData.salesTaxRate[stateCode]
        };
    }
    
    getNationalAverages() {
        // Calculate national averages for all metrics
        const states = Object.keys(this.realData.population);
        const avgCalc = (metric) => {
            const values = states.map(s => this.realData[metric][s]).filter(v => v != null);
            return values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
        };
        
        return {
            population: avgCalc('population'),
            populationDensity: avgCalc('populationDensity'),
            medianAge: avgCalc('medianAge'),
            medianHouseholdIncome: avgCalc('medianHouseholdIncome'),
            povertyRate: avgCalc('povertyRate'),
            unemploymentRate: avgCalc('unemploymentRate'),
            gdpPerCapita: avgCalc('gdpPerCapita'),
            medianHomeValue: avgCalc('medianHomeValue'),
            medianRent: avgCalc('medianRent'),
            homeownershipRate: avgCalc('homeownershipRate'),
            educationBachelor: avgCalc('educationBachelor'),
            highSchoolGraduation: avgCalc('highSchoolGraduation'),
            uninsuredRate: avgCalc('uninsuredRate'),
            lifeExpectancy: avgCalc('lifeExpectancy'),
            violentCrimeRate: avgCalc('violentCrimeRate'),
            propertyCrimeRate: avgCalc('propertyCrimeRate'),
            costOfLivingIndex: 100, // By definition
            salesTaxRate: avgCalc('salesTaxRate'),
            stateIncomeTaxRate: avgCalc('stateIncomeTaxRate'),
            averageCommuteTime: avgCalc('averageCommuteTime')
        };
    }
    
    getStateRank(stateCode, metric, inverse = false) {
        if (!stateCode || !metric || !this.realData[metric]) return 'N/A';
        
        const states = Object.keys(this.realData[metric]);
        const values = states.map(s => ({
            state: s,
            value: this.realData[metric][s]
        })).filter(v => v.value != null);
        
        // Sort by value (higher is better unless inverse)
        values.sort((a, b) => {
            if (inverse) {
                return a.value - b.value; // Lower is better
            }
            return b.value - a.value; // Higher is better  
        });
        
        const rank = values.findIndex(v => v.state === stateCode) + 1;
        return rank || 'N/A';
    }
}

// Create singleton instance
const dataService = new DataService();

export default dataService;